import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Alert } from '../components/ui/Alert';
import { useAcceptInvite } from '../hooks/useUsers';
import { ArrowRight } from 'lucide-react';

export function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const acceptInvite = useAcceptInvite();

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!token) {
      setError('Invalid invitation link');
      return;
    }

    acceptInvite.mutate(
      { token, name, password },
      {
        onSuccess: () => {
          navigate('/login', {
            state: { message: 'Account created! Please log in.' },
          });
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : 'Failed to accept invitation');
        },
      }
    );
  };

  return (
    <div className="min-h-screen flex bg-surface-secondary">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8 flex flex-col items-center text-center">
            <div className="flex items-start gap-1.5">
              <img
                src="/sequesto-logo-dark.png"
                alt="SEQUESTO"
                className="h-4 w-auto select-none"
                draggable={false}
              />
              <span className="eyebrow mt-0.5">OS</span>
            </div>
            <h1 className="mt-8 font-display text-display-sm text-text-primary">
              Accept your invitation
            </h1>
            <p className="mt-2 text-body-md text-text-tertiary">
              Create your account to join the team
            </p>
          </div>

          <div className="rounded-3xl bg-surface-primary p-8 border border-border-light shadow-soft">
          {error && (
            <Alert variant="error" className="mb-6" dismissible onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              required
            />

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />

            <Input
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={acceptInvite.isPending}
              rightIcon={
                !acceptInvite.isPending && (
                  <ArrowRight className="h-4 w-4" />
                )
              }
            >
              {acceptInvite.isPending ? 'Creating account…' : 'Create account'}
            </Button>
          </form>
          </div>

          <p className="text-center text-body-sm text-text-tertiary mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
